import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';
import type { TarjetaAyuda } from '../../marco/cabecera';
import type { EstadoCita, EstadoCobroCita, EstadoConsentimiento, MotivoCancelacion } from '../../api/ops/citas';

/**
 * Textos de la Agenda (9.1), Nueva cita (9.2), la ficha de la cita (9.3) y Mi agenda (9.8), tal cual el diseño
 * (`transicion/diseno/citas/9.1-9.3-9.8-agenda-y-citas.html`).
 */

/** Permisos que miran estas pantallas (solo para no enseñar botones que van a fallar: la API decide). */
export const PERMISOS = {
    ver: 'citas.agenda.ver',
    cifras: 'citas.agenda.ver_agregado',
    gestionar: 'citas.citas.gestionar',
    forzar: 'citas.citas.forzar',
    completar: 'citas.citas.completar',
    noShow: 'citas.no_shows.marcar',
    deshacerNoShow: 'citas.no_shows.deshacer',
    llegada: 'pases.puerta.validar',
    pases: 'pases.pases.ver',
    verDocumento: 'documentos.documentos.ver',
    bloqueados: 'documentos.bloqueados.ver',
    recogerFirma: 'documentos.firmas.recoger',
    reintentarCobro: 'pagos.cobros.crear',
    verPagos: 'pagos.pagos.ver',
    verClientes: 'clientes.fichas.ver',
    crearClientes: 'clientes.fichas.crear',
    horarioVer: 'disponibilidad.horarios.ver',
    horarioGestionar: 'disponibilidad.horarios.gestionar',
} as const;

/** Estado de la cita: texto del chip y su color (ST del diseño). */
export const ESTADO: Readonly<Record<EstadoCita, readonly [string, EstadoChip]>> = {
    pending_payment: ['Pendiente de pago', 'pending'],
    confirmed: ['Confirmada', 'confirmed'],
    arrived: ['Llegó', 'confirmed'],
    pending_completion: ['Por completar', 'pending'],
    completed: ['Completada', 'confirmed'],
    no_show: ['No presentado', 'problem'],
    cancelled: ['Cancelada', 'problem'],
    expired: ['Caducada', 'problem'],
};

/** Estado del cobro (PAYST del diseño). */
export const COBRO: Readonly<Record<EstadoCobroCita, readonly [string, EstadoChip]>> = {
    not_required: ['Sin señal', 'neutral'],
    pending: ['Pendiente', 'pending'],
    paid: ['Señal pagada', 'confirmed'],
    guarantee_saved: ['Garantía guardada', 'neutral'],
    refunded: ['Reembolsado', 'neutral'],
    partially_refunded: ['Reembolsado en parte', 'neutral'],
    in_dispute: ['En disputa', 'problem'],
    charge_failed: ['Cobro fallido', 'problem'],
};

/** Estado de la firma del consentimiento (CONS del diseño). */
export const FIRMA: Readonly<Partial<Record<EstadoConsentimiento, readonly [string, EstadoChip]>>> = {
    signed: ['Firmado', 'confirmed'],
    requested: ['Enlace enviado', 'pending'],
    pending: ['Firma pendiente', 'pending'],
    voided: ['Anulado', 'problem'],
};

/** Motivos de cancelación que ofrece la ventana (REASONS del diseño). */
export const MOTIVOS: Readonly<Partial<Record<MotivoCancelacion, string>>> = {
    customer_request: 'Lo pide la clienta',
    business: 'Decisión del negocio',
    professional_unavailable: 'El profesional no puede',
    other: 'Otro motivo',
};

export const MOTIVOS_NEGOCIO: readonly MotivoCancelacion[] = ['business', 'customer_request', 'professional_unavailable', 'other'];
export const MOTIVOS_PROFESIONAL: readonly MotivoCancelacion[] = ['professional_unavailable', 'customer_request', 'other'];

/** Por dónde se pidió la cita (CH del diseño). */
export const CANAL: Readonly<Record<string, string>> = { web: 'Web', panel: 'Panel', widget: 'Widget', assistant: 'Asistente', tablet: 'Tablet', api: 'Integración' };

/** Quién canceló (texto de la caja de la ficha). */
export const CANCELADA_POR: Readonly<Record<string, string>> = { customer: 'por la clienta', business: 'por el negocio', professional: 'por el profesional', system: 'por el sistema' };

const t = (icono: NombreIcono, titulo: string, texto: string): TarjetaAyuda => ({ icono, titulo, texto });

/** Tarjetas de la «?» (HELP del diseño). */
export const AYUDA = {
    agenda: [
        t('calendar-days', 'Tu agenda', 'Una columna por profesional. Toca un hueco libre para darle una cita, o una cita para ver su ficha.'),
        t('circle-alert', 'Por completar', 'Las citas que terminan sin que nadie marque la llegada esperan a que digas si vino. Nada se completa solo.'),
        t('user-x', 'No vino', 'Lo marca siempre una persona, desde la hora de inicio más 15 minutos. Si tu política lo cobra, avisamos a la clienta antes.'),
        t('credit-card', 'Señal', 'Las citas que crees aquí siguen tu política de cobro: le llega el enlace de pago y tiene 30 minutos.'),
    ],
    movil: [
        t('list', 'Por hora', 'Elige un profesional para ver sus huecos libres, o «Todos» para ver todas las citas por hora.'),
        t('circle-alert', 'Por completar', 'Las citas que terminan sin llegada esperan a que digas si vino. Nada se completa solo.'),
        t('log-in', 'Llegó', 'Márcalo con un toque al recibirla. Si te equivocas, tienes 5 segundos para deshacerlo.'),
    ],
    mia: [
        t('calendar-days', 'Mi agenda', 'Solo tus citas. Marca con un toque la llegada y, al terminar, si vino o no vino.'),
        t('circle-alert', 'Por completar', 'Si una cita termina sin llegada, queda aquí hasta que digas si vino. Nada se completa solo.'),
        t('signature', 'Consentimientos', 'Ves los firmados de tus citas. Si falta una firma, se puede firmar en la tablet del centro.'),
    ],
    horario: [
        t('clock', 'Mi horario', 'Tu semana tipo: los tramos en que trabajas cada día. La pausa es el hueco entre dos tramos.'),
        t('history', 'Desde cuándo', 'El cambio vale desde la fecha que elijas; el horario anterior se guarda hasta la víspera.'),
        t('users', 'Citas que quedan fuera', 'Si el cambio deja fuera alguna cita, lo confirma el negocio y avisa a las clientas.'),
    ],
    socio: [t('chart-column', 'Cifras', 'Citas, horas reservadas, completadas, no presentados y canceladas por profesional. Sin datos de los clientes.')],
    cita: [
        t('calendar-check', 'La cita', 'Registra la llegada al recibirla. Al terminar sin llegada, queda por completar hasta que digas si vino.'),
        t('pencil', 'Cambiar', 'Busca otro hueco. Le avisamos del cambio; lo que ha pagado se mantiene.'),
        t('ban', 'Cancelar', 'Nada se borra: queda cancelada con su motivo. Si cancela el negocio, se le devuelve todo.'),
    ],
    cobro: [
        t('credit-card', 'Cobro', 'La señal, la garantía y los cargos de la cita, según la política con la que se pidió.'),
        t('refresh-cw', 'Cobro fallido', 'Se reintenta solo 3 veces como mucho y te avisamos si falla. Puedes reintentarlo a mano.'),
    ],
    firma: [
        t('signature', 'Consentimiento', 'Se pide solo al confirmar la cita. Si ya firmó uno vigente, se reutiliza.'),
        t('tablet', 'En el centro', 'Si no firmó a tiempo, firma en la tablet antes de empezar. La cita no se cancela.'),
    ],
    historial: [t('history', 'Historial', 'Todo lo que le ha pasado a la cita, quién lo hizo y desde dónde.')],
    nueva: [
        t('scissors', 'Servicios', 'Elige uno o varios: se hacen seguidos y la duración se suma.'),
        t('user', 'Profesional', '«Cualquiera» reparte al que esté menos ocupado ese día.'),
        t('credit-card', 'Señal', 'Con señal, la cita queda pendiente de pago 30 minutos. Si no paga, el hueco se libera.'),
    ],
    cambiar: [
        t('calendar-clock', 'Otro hueco', 'Solo ves los huecos donde caben sus servicios. Su propio hueco cuenta como libre.'),
        t('send', 'Aviso', 'Le avisamos del cambio por su canal. Lo que ha pagado se mantiene.'),
    ],
    dia: [
        t('clock', 'Tramos', 'Si termina antes de empezar (de 20:00 a 02:00), acaba al día siguiente.'),
        t('pencil', 'Se guarda junto', 'Se aplica al pulsar «Guardar horario», con su fecha de efecto.'),
    ],
} as const;

/** Apartados de la ficha de la cita (`?tab=`, G779). */
export type PestanaCita = 'cita' | 'cobro' | 'firma' | 'historial';
export const PESTANAS: readonly (readonly [PestanaCita, string, NombreIcono])[] = [
    ['cita', 'Cita', 'calendar-check'],
    ['cobro', 'Cobro', 'credit-card'],
    ['firma', 'Firma', 'signature'],
    ['historial', 'Historial', 'history'],
];

export function esPestana(v: string | null): v is PestanaCita {
    return v === 'cita' || v === 'cobro' || v === 'firma' || v === 'historial';
}
